import { Button, Dialog, Input } from "@repo/ui";
import { motPluriel, pluriel } from "@repo/utils";
import { useMutation } from "convex/react";
import { Loader2, Plus, RefreshCw, Trash2 } from "lucide-react";
import { type FormEvent, type ReactNode, useState } from "react";
import { api } from "../../../convex/_generated/api";
import type { Id } from "../../../convex/_generated/dataModel";

interface CandidatRow {
  emailCandidat: string;
  nomCandidat: string;
  prenomCandidat: string;
  token: string;
}

/**
 * En-tête de colonne des lignes candidat, rendu sur la PREMIÈRE ligne seulement.
 *
 * Ce n'est volontairement pas un `<Label>` : il surplombe une colonne entière,
 * donc il ne peut désigner le champ d'aucune ligne en particulier — et surtout
 * pas ceux des lignes suivantes, qui n'ont aucun libellé visible. Chaque champ
 * porte donc son propre `aria-label`, et la ligne son nom de groupe
 * (« Candidat 2 ») : un lecteur d'écran annonce « Candidat 2, Prénom ».
 */
function EnTeteColonne({ children }: { children: ReactNode }) {
  return (
    <span
      aria-hidden="true"
      className="text-xs leading-none font-medium select-none"
    >
      {children}
    </span>
  );
}

interface AjouterCandidatsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  campagneId: Id<"campagnes">;
  campagneNom: string;
  anonyme?: boolean;
  /** Portail admin : mutation cross-org, sans rattachement à l'org de l'appelant. */
  superAdmin?: boolean;
}

export function AjouterCandidatsDialog({
  isOpen,
  onClose,
  campagneId,
  campagneNom,
  anonyme = false,
  superAdmin = false,
}: AjouterCandidatsDialogProps) {
  const [candidats, setCandidats] = useState<CandidatRow[]>([
    { emailCandidat: "", nomCandidat: "", prenomCandidat: "", token: "" },
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const ajouterCandidatsClient = useMutation(api.campagnes.ajouterCandidats);
  const ajouterCandidatsSuperAdmin = useMutation(api.superAdmin.ajouterCandidatsSuperAdmin);
  const ajouterCandidats = superAdmin ? ajouterCandidatsSuperAdmin : ajouterCandidatsClient;

  const reset = () => {
    setCandidats([{ emailCandidat: "", nomCandidat: "", prenomCandidat: "", token: "" }]);
    setError(null);
    setSuccess(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const genererToken = (index: number) => {
    const chars = "abcdefghijklmnopqrstuvwxyz0123456789";
    const part = (n: number) =>
      Array.from({ length: n }, () => chars[Math.floor(Math.random() * chars.length)]).join("");
    const newToken = `${part(4)}-${part(4)}`;
    setCandidats((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index]!, token: newToken };
      return updated;
    });
  };

  const addCandidatRow = () => {
    setCandidats((prev) => [
      ...prev,
      { emailCandidat: "", nomCandidat: "", prenomCandidat: "", token: "" },
    ]);
  };

  const removeCandidatRow = (index: number) => {
    setCandidats((prev) => prev.filter((_, i) => i !== index));
  };

  const updateCandidat = (index: number, field: keyof CandidatRow, value: string) => {
    setCandidats((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index]!, [field]: value };
      return updated;
    });
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const validCandidats = candidats.filter((c) =>
        anonyme ? c.token.trim() : c.emailCandidat.trim() && c.nomCandidat.trim() && c.prenomCandidat.trim(),
      );

      if (validCandidats.length === 0) {
        throw new Error("Ajoutez au moins un candidat");
      }

      const result = await ajouterCandidats({
        campagneId,
        candidats: validCandidats.map((c) => ({
          emailCandidat: c.emailCandidat,
          nomCandidat: anonyme ? c.token : c.nomCandidat,
          prenomCandidat: c.prenomCandidat,
          token: c.token || undefined,
        })),
      });

      setSuccess(`${pluriel(result.length, "candidat")} ${motPluriel(result.length, "ajouté")} à la campagne`);
      setCandidats([{ emailCandidat: "", nomCandidat: "", prenomCandidat: "", token: "" }]);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsLoading(false);
    }
  };

  const nbValides = candidats.filter((c) =>
    anonyme ? c.token : c.emailCandidat && c.nomCandidat && c.prenomCandidat,
  ).length;

  return (
    <Dialog
      isOpen={isOpen}
      onClose={handleClose}
      title={`Ajouter des candidats — ${campagneNom}`}
      className="max-w-3xl"
      closeOnOutsideClick={false}
    >
      <form onSubmit={handleSubmit} className="grid gap-4 p-1">
        {error && (
          <p className="text-sm text-etat-erreur bg-etat-erreur-surface p-2 rounded-surface">{error}</p>
        )}
        {success && (
          <p className="text-sm text-etat-fait bg-etat-fait-surface p-2 rounded-surface">
            {success}
          </p>
        )}

        <div className="flex items-center justify-between">
          {/* `<span>` et non `<Label>` : cet intitulé désigne la section, pas un
              champ. Un `<label>` sans cible est un libellé orphelin. */}
          <span className="flex items-center gap-2 text-sm leading-none font-medium select-none">
            Candidats ({nbValides})
          </span>
        </div>

        <div className="max-h-64 overflow-y-auto space-y-2 py-1 px-1 -mx-1">
          {candidats.map((c, index) => (
            <div
              key={index}
              role="group"
              aria-label={`Candidat ${index + 1}`}
              className="flex items-end gap-2"
            >
              {!anonyme && (
                <div className="grid gap-1 flex-1">
                  {index === 0 && <EnTeteColonne>Prénom</EnTeteColonne>}
                  <Input
                    aria-label="Prénom"
                    value={c.prenomCandidat}
                    onChange={(e) => updateCandidat(index, "prenomCandidat", e.target.value)}
                    placeholder="Prénom"
                  />
                </div>
              )}
              {!anonyme && (
                <div className="grid gap-1 flex-1">
                  {index === 0 && <EnTeteColonne>Nom</EnTeteColonne>}
                  <Input
                    aria-label="Nom"
                    value={c.nomCandidat}
                    onChange={(e) => updateCandidat(index, "nomCandidat", e.target.value)}
                    placeholder="Nom"
                  />
                </div>
              )}
              {!anonyme && (
                <div className="grid gap-1 flex-1">
                  {index === 0 && <EnTeteColonne>Email</EnTeteColonne>}
                  <Input
                    aria-label="Email"
                    type="email"
                    value={c.emailCandidat}
                    onChange={(e) => updateCandidat(index, "emailCandidat", e.target.value)}
                    placeholder="Email"
                  />
                </div>
              )}
              {anonyme && (
                <div className="grid gap-1 flex-1">
                  {index === 0 && <EnTeteColonne>Token</EnTeteColonne>}
                  <div className="flex gap-1">
                    <Input
                      aria-label="Token"
                      value={c.token}
                      onChange={(e) => updateCandidat(index, "token", e.target.value)}
                      placeholder="abc-1234"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      aria-label={`Générer un nouveau code pour le candidat ${index + 1}`}
                      onClick={() => genererToken(index)}
                    >
                      <RefreshCw className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              )}
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={`Retirer le candidat ${index + 1}`}
                onClick={() => removeCandidatRow(index)}
                disabled={candidats.length <= 1}
              >
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </div>
          ))}
        </div>

        <Button type="button" variant="outline" size="sm" onClick={addCandidatRow} className="w-full">
          <Plus className="h-4 w-4 mr-1" />
          Ajouter une ligne
        </Button>

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={handleClose}>
            Fermer
          </Button>
          <Button type="submit" disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Ajout en cours...
              </>
            ) : (
              "Ajouter les candidats"
            )}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
